import React, { useCallback, useEffect, useRef, useState } from "react";

import dynamic from "next/dynamic";

import { HEADER_PINNED, HEADER_UNPINNED } from "@/common/static/header.static";
import { HeaderDesktop } from "@/components/Header/HeaderDesktop";
import { HeaderEventsBanner } from "@/components/Header/HeaderEventsBanner";
import { HeaderMobile } from "@/components/Header/HeaderMobile";
import { Headroom } from "@/components/Header/Headroom";
import HeaderBanner from "@/components/MainHeader/HeaderBanner";

/*
  The embedded-message SDK is ~320KB of JS and the banner renders nothing
  until it has fetched messages on the client, so there is nothing to
  server-render. `ssr: false` keeps its chunk out of the document's
  eagerly-deferred script set and off the first-paint critical path.
*/
const HomepageTopEmbeddedMessages = dynamic(
  () =>
    import("@/features/iterable/components/HomepageTopEmbeddedMessages").then(
      module => module.HomepageTopEmbeddedMessages
    ),
  { ssr: false }
);

const HEADER_STYLE: React.CSSProperties = {
  zIndex: 100,
};

type HeaderProps = {
  focused?: boolean;
  hideBanner?: boolean;
};

const HEADER_PIN_EVENT = new Event(HEADER_PINNED);
const HEADER_UNPIN_EVENT = new Event(HEADER_UNPINNED);

type HeaderBannerWrapperProps = {
  focused?: boolean;
  hideBanner?: boolean;
  onMounted?: (height: number) => void;
};

const HeaderBannerWrapper = ({
  focused = false,
  hideBanner = false,
  onMounted,
}: HeaderBannerWrapperProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleTopEmbeddedMessagesHeightChange = useCallback(() => {
    if (ref.current) {
      onMounted?.(ref.current.getBoundingClientRect().height);
    }
  }, [onMounted]);

  useEffect(() => {
    handleTopEmbeddedMessagesHeightChange();
  }, [handleTopEmbeddedMessagesHeightChange]);

  if (hideBanner) {
    return null;
  }

  return (
    <div ref={ref} className="flex flex-col">
      <HeaderEventsBanner />
      <HeaderBanner.Desktop focused={focused} />
      <HeaderBanner.Mobile focused={focused} />
      <HomepageTopEmbeddedMessages
        onHeightChange={handleTopEmbeddedMessagesHeightChange}
      />
    </div>
  );
};

const Header = ({
  focused = false,
  hideBanner = false,
  className,
}: HeaderProps & { className?: string }) => {
  const [pinStart, setPinStart] = useState<number | undefined>(undefined);

  const handleBannerMounted = (height: number) => {
    setPinStart(height);
  };

  const handlePin = () => {
    document.dispatchEvent(HEADER_PIN_EVENT);
  };

  const handleUnpin = () => {
    document.dispatchEvent(HEADER_UNPIN_EVENT);
  };

  return (
    <>
      <HeaderBannerWrapper
        focused={focused}
        hideBanner={hideBanner}
        onMounted={handleBannerMounted}
      />
      <Headroom
        onPin={handlePin}
        onUnpin={handleUnpin}
        id="main-header"
        style={HEADER_STYLE}
        className={className}
        pinStart={pinStart}
      >
        <HeaderDesktop focused={focused} />
        <HeaderMobile focused={focused} />
      </Headroom>
    </>
  );
};

export default Header;
